import React, { useEffect, useState } from "react";
import { BudgetDataItem, InputOption } from "../../types.ts";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { budgetAtom } from "../../hook/BudgetAtom.ts";
import { useAtom, useAtomValue } from "jotai";
import * as S from "./monthly.style.ts";
import BudgetItem from "../../views/BudgetItem/BudgetItem.tsx";
import {
  budgetSortOptions,
  listOfBudgets,
  listOfMonths,
  monthSelect,
} from "../../constants.ts";
import {
  addNewBudgetItem,
  getMonthlyTotalAmount,
  reformatBudgetItem,
  sortBudget,
} from "../../functions/budget.ts";
import Button from "../../components/Button/Button.tsx";
import AddIcon from "../../svg/AddIcon.tsx";
import ModalComponent from "../../components/Modal/Modal.tsx";
import {
  capitalizePageTitle,
  checkIsExpiredSession,
} from "../../functions/helper.ts";
import ErrorPage from "../../views/ErrorPage/ErrorPage.tsx";
import {
  addBudgetItem,
  deleteBudgetItem,
  updateBudgetItem,
} from "../../requests/budget.ts";
import { useAuth } from "../../context/AuthContext.tsx";
import BudgetDetails from "../../views/BudgetDetails/BudgetDetails.tsx";
import Loading from "../../components/Loading/Loading.tsx";
import SelectComponent from "../../components/Select/Select.tsx";
import { userAtom } from "../../hook/UserAtom.ts";
import { incomeEventsAtom } from "../../hook/IncomeEventsAtom.ts";
import { recurringIncomeAtom } from "../../hook/RecurringIncomeAtom.ts";
import {
  expandRecurringIncome,
  getMonthlyIncomeSeries,
} from "../../functions/cashflow.ts";
import { trackError, trackEvent } from "../../functions/mixpanel.ts";
import SessionExpired from "../../components/SessionExpired/SessionExpired.tsx";
import FilterIcon from "../../svg/FilterIcon.tsx";
import ViewIcon from "../../svg/ViewIcon.tsx";

const Monthly = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth();
  const [budget, setBudget] = useAtom(budgetAtom);
  const [currentUser, setCurrentUser] = useAtom(userAtom);
  const incomeEvents = useAtomValue(incomeEventsAtom);
  const recurringIncome = useAtomValue(recurringIncomeAtom);
  const clonedBudget = [...budget];
  const { type, month, year } = useParams();
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    type,
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [isNewBudget, setIsNewBudget] = useState<boolean>(false);
  const [selectedSort, setSelectedSort] = useState<string>(
    currentUser?.selectedSort || budgetSortOptions[0].label,
  );
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

  useEffect(() => {
    type === "expense" && selectedOption !== "goals" && setSelectedOption(type);
  }, [type]);

  if (
    !type ||
    !month ||
    !year ||
    !listOfBudgets.includes(type) ||
    !listOfMonths.includes(month) ||
    isNaN(Number(year))
  ) {
    return <ErrorPage />;
  }

  // Income is now a dated log rather than a per-month figure — send the old
  // monthly income view to the income page.
  if (type === "income") {
    return <Navigate to="/income" replace />;
  }

  const theYear = Number(year);

  // Income lives in the dated log now, not the budget — pull this month's
  // received + weighted-expected total for the overview.
  const monthSeries = getMonthlyIncomeSeries(
    [
      ...incomeEvents,
      ...expandRecurringIncome(recurringIncome, { month, year: theYear }, 1),
    ],
    { month, year: theYear },
    1,
  )[0];
  const totalIncome = monthSeries
    ? monthSeries.received + monthSeries.expectedWeighted
    : 0;
  const totalExpense = getMonthlyTotalAmount(budget, month, theYear, "expense");
  const budgetIndex = clonedBudget.findIndex(
    (item) => item.year === theYear && item.month === month,
  );

  const handleAddNewBudget = () => {
    const updatedBudget = addNewBudgetItem(clonedBudget, month, theYear, type);

    setBudget(updatedBudget);
    setIsNewBudget(true);
  };

  const handleSaveEvent = async (
    obj: Object,
    data?: BudgetDataItem,
    isPaid?: boolean,
    frequency?: string,
    cadence?: string,
    category_id?: number,
  ) => {
    setIsLoadingData(true);
    const updatedItem = reformatBudgetItem(
      obj,
      data?.budget_id || null,
      data?.budget_date_id || null,
      month,
      theYear,
      isPaid,
      frequency,
      cadence,
      category_id,
    );

    try {
      const accessToken = await getAccessTokenSilently();

      if (!!data?.budget_id) {
        const updatedBudget = await updateBudgetItem(
          accessToken,
          updatedItem[0],
        );

        setBudget(updatedBudget.budget);
        trackEvent(`Edit ${type}`);
        setIsLoadingData(false);
      } else {
        updatedItem[0].type = type;
        const addedBudget = await addBudgetItem(accessToken, updatedItem[0]);

        setBudget(addedBudget.budget);
        trackEvent(`Add New ${type}`);
        setIsLoadingData(false);
      }

      setIsNewBudget(false);
    } catch (err) {
      setIsLoadingData(false);
      trackError("Monthly - handleSaveEvent:", {
        result: err,
      });

      if (checkIsExpiredSession(err)) {
        setIsSessionExpired(true);
      }
    }
  };

  const handleDeleteEvent = async (
    currentItems: BudgetDataItem[],
    data: BudgetDataItem,
  ) => {
    if (currentItems.length === 1) {
      setIsOpen(true);
      return;
    }
    setIsLoadingData(true);

    try {
      const accessToken = await getAccessTokenSilently();

      if (!!data.budget_id) {
        const deletedBudget = await deleteBudgetItem(
          accessToken,
          data.budget_id,
        );
        setBudget(deletedBudget.budget);

        trackEvent(`Delete ${type}`);
      }

      setIsNewBudget(false);
      setIsLoadingData(false);
    } catch (err) {
      setIsLoadingData(false);
      trackError("Monthly - handleDeleteEvent:", {
        result: err,
      });

      if (checkIsExpiredSession(err)) {
        setIsSessionExpired(true);
      }
    }
  };

  if (!currentUser?.hasBudget) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <S.MonthlyWrapper>
      <S.Title>{`${month} ${theYear}`}</S.Title>
      <S.ContentWrapper>
        {selectedOption === type && (
          <>
            <S.BudgetOptions>
              <Button
                buttonSize="small"
                classType="text"
                handleClick={() => setIsFilterOpen(true)}
              >
                <>
                  <FilterIcon /> Filter
                </>
              </Button>
              <Button
                buttonSize="small"
                classType="text"
                handleClick={() => setIsOverviewOpen(true)}
              >
                <>
                  <ViewIcon /> Overview
                </>
              </Button>
            </S.BudgetOptions>
            <S.ItemWrapper>
              <S.ItemContainer>
                {!budget.length && <Loading isText />}
                {!!budget.length &&
                  budget[budgetIndex][type]
                    .sort((a: BudgetDataItem, b: BudgetDataItem) =>
                      sortBudget(a, b, selectedSort),
                    )
                    .map((data: BudgetDataItem, i: number) => {
                      if (data.label === "" && !data.budget_id && !data.temp) {
                        return {};
                      }

                      const currentItems: BudgetDataItem[] = [
                        ...budget[budgetIndex][type],
                      ];

                      return (
                        <BudgetItem
                          key={i}
                          theType={type as InputOption}
                          item={data}
                          labelPlaceHolder={`${type.toLowerCase()} name`}
                          valuePlaceHolder={`${type.toLowerCase()} (USD)`}
                          inputType="number"
                          budgetItemData={budget[budgetIndex]}
                          saveEvent={handleSaveEvent}
                          deleteEvent={() =>
                            handleDeleteEvent(currentItems, data)
                          }
                          showPaidContent
                          openModal={isNewBudget}
                          index={i}
                        />
                      );
                    })}
                <ModalComponent
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  title={`Want to remove the last ${type}?`}
                >
                  <S.ModalWrapper>
                    <span>
                      You can't delete this {type} because it is the only one
                      you have left. Please edit it instead.
                    </span>
                    <Button
                      buttonSize="small"
                      handleClick={() => setIsOpen(false)}
                      classType="exit"
                    >
                      Close
                    </Button>
                  </S.ModalWrapper>
                </ModalComponent>
              </S.ItemContainer>
            </S.ItemWrapper>
            <S.AddBtn>
              <Button buttonSize="medium" handleClick={handleAddNewBudget}>
                <>
                  <AddIcon /> {`Add ${type}`}
                </>
              </Button>
            </S.AddBtn>
          </>
        )}
      </S.ContentWrapper>
      <SessionExpired
        isOpen={isSessionExpired}
        closeModal={setIsSessionExpired}
      />
      <ModalComponent
        isOpen={isOverviewOpen}
        onClose={() => setIsOverviewOpen(false)}
        title={`${capitalizePageTitle(month)} Overview`}
        size="medium"
      >
        <S.ModalWrapper>
          <BudgetDetails income={totalIncome} expense={totalExpense} />
          <S.ModalBtn>
            <Button
              buttonSize="small"
              handleClick={() => setIsOverviewOpen(false)}
              classType="exit"
            >
              Close
            </Button>
          </S.ModalBtn>
        </S.ModalWrapper>
      </ModalComponent>
      <ModalComponent
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filter Budget"
        size="medium"
      >
        <S.ModalWrapper>
          <S.Selectors>
            <SelectComponent
              options={monthSelect}
              placeHolder="Change Month"
              defaultValue={month}
              setOption={(val) => {
                navigate(`/monthly/expense/${val}/${theYear}`);
                setIsFilterOpen(false);
              }}
            />
            <SelectComponent
              options={budgetSortOptions}
              placeHolder="Sort Items"
              defaultValue={currentUser?.selectedSort || selectedSort}
              setOption={(val) => {
                setSelectedSort(val);

                currentUser &&
                  setCurrentUser({
                    ...currentUser,
                    selectedSort: val,
                  });
              }}
            />
          </S.Selectors>
          <S.ModalBtn>
            <Button
              buttonSize="small"
              handleClick={() => setIsFilterOpen(false)}
              classType="exit"
            >
              Close
            </Button>
          </S.ModalBtn>
        </S.ModalWrapper>
      </ModalComponent>
      <ModalComponent isOpen={isLoadingData} title="Loading..." size="medium">
        <S.ModalWrapper>
          Your budget is being updated. Please wait for a moment.
        </S.ModalWrapper>
      </ModalComponent>
    </S.MonthlyWrapper>
  );
};

export default Monthly;
